<script setup lang="tsx">
import { computed, onMounted, ref } from 'vue'
import http from '@/services/http'
import toastStore from '@/stores/toast'
import type { User, ResponsibleFor, Response, Role } from '@/models/api-generated'
import { useI18n } from 'vue-i18n'
import { refreshCallback } from './shared'
import { roleRefreshCallback } from '../role/shared'
import { useTheme } from './useTheme'
import ResponsibleForTabs from './ResponsibleForTabs.vue'

const { theme } = useTheme()

const toggleTheme = () => {
	theme.value = theme.value === 'light' ? 'dark' : 'light'
}

const { t, locale } = useI18n()
const toaster = toastStore()

const user = ref<User>(sessionMap.user)
const password2 = ref('')
const language = ref(sessionMap.language)

const isValidFirstname = computed(() => user.value.firstname.length > 0)
const isValidLastname = computed(() => user.value.lastname.length > 0)
const isValidEmail = computed(() => user.value.email.length > 2 && user.value.email.includes('@'))
const isValidIdCode = computed(() => user.value.idCode.length === 11)
const isValidPassword = computed(() => user.value.password.length === 0 || user.value.password.length >= 12)
const isValidPassword2 = computed(() => isValidPassword.value && user.value.password === password2.value)

const isUniqueUser = computed(() => {
	return isValidEmail.value || isValidIdCode.value
})

const canSave = computed(() => {
	return isUniqueUser.value && isValidFirstname.value && isValidLastname.value && isValidPassword2.value
})

function reset() {
	user.value = sessionMap.user
	password2.value = ''
	language.value = sessionMap.language
}

async function save() {
	const { status } = await http.post(`api/user?lang=${language.value}`, user.value)
	if (status === 200) {
		locale.value = language.value
		toaster.showSuccess(t('misc.successfullyEdited'))
		if (refreshCallback.value) refreshCallback.value()
		roleRefreshCallback.value?.()
	}
}

const responsibleFor = ref<ResponsibleFor>()

interface ResponsibleForRole extends ResponsibleFor {
	role: Role
}
const responsibleForRoles = ref<ResponsibleForRole[]>([])

onMounted(() => {
	fetch()
})

async function fetch() {
	const { data } = await http.get<Response<ResponsibleFor>>(`api/responsibleFor/users/${user.value.id}`)
	responsibleFor.value = data.data

	for (const role of user.value.roles) {
		const { data } = await http.get<Response<ResponsibleForRole>>(`api/responsibleFor/roles/${role.id}`)
		if (data.data) {
			data.data.role = role
			responsibleForRoles.value.push(data.data)
		}
	}
}
</script>

<template>
	<div class="row">
		<div class="col-md-6">
			<div class="card mb-3">
				<div class="card-body">
					<div class="row">
						<label for="user-aduser" class="col-4 col-form-label">{{ $t('adUser') }}</label>
						<div class="col-8 d-flex justify-content-end">
							<span class="align-self-center">{{ user.adUser.length ? user.adUser : '-' }}</span>
						</div>
					</div>

					<div class="row">
						<label for="user-audit" class="col-4 col-form-label">{{ $t('role', 2) }}</label>
						<div class="col-8 d-flex justify-content-end">
							<div class="text-end w-100">
								<template v-if="user.roles.length">
									<div v-for="role in user.roles" :key="role.id" class="w-100">
										{{ role.name }}
									</div>
								</template>
								<template v-else>-</template>
							</div>
						</div>
					</div>
					<div class="row mt-2">
						<label for="user-audit" class="col-4 col-form-label">{{ $t('misc.theme') }}</label>
						<div class="col-8 d-flex justify-content-end">
							<button class="btn btn-outline-secondary btn-sm mb-2" @click="toggleTheme">
								<i :class="theme === 'dark' ? 'bi bi-moon' : 'bi bi-sun'"></i>
							</button>
						</div>
					</div>
				</div>
			</div>

			<div class="card mb-3">
				<div class="card-body">
					<h4 class="text-center">{{ $t('risk.responsible', 2) }}</h4>
					<ResponsibleForTabs :responsible-for="responsibleFor" :title="user.label" type="User" />
					<div v-for="(r, index) in responsibleForRoles" :key="index">
						<ResponsibleForTabs :responsible-for="r" :title="r.role.name" type="Role" />
					</div>
				</div>
			</div>
		</div>

		<div class="col-md-6">
			<div class="card mb-3">
				<div class="card-body">
					<div class="row">
						<label for="user-language" class="col-4 col-form-label">{{ $t('language') }}</label>
						<div class="col-8 d-flex justify-content-end">
							<select data-test="user-language" v-model="language" class="form-select mb-2">
								<option value="et">{{ $t('estonian') }}</option>
								<option value="en">{{ $t('english') }}</option>
							</select>
						</div>
					</div>

					<div class="row">
						<label for="user-firstname" class="col-4 col-form-label">{{ $t('firstname') }}</label>
						<div class="col-8">
							<input
								id="user-firstname"
								class="form-control mb-2"
								type="text"
								maxlength="100"
								v-model="user.firstname"
								:class="{ 'is-invalid': !isValidFirstname }"
							/>
							<div class="invalid-feedback">
								{{ $t('misc.validField') }}
							</div>
						</div>
					</div>

					<div class="row">
						<label for="user-lastname" class="col-4 col-form-label">{{ $t('lastname') }}</label>
						<div class="col-8">
							<input
								id="user-lastname"
								class="form-control mb-2"
								type="text"
								maxlength="100"
								v-model="user.lastname"
								:class="{ 'is-invalid': !isValidLastname }"
							/>
							<div class="invalid-feedback">
								{{ $t('misc.validField') }}
							</div>
						</div>
					</div>

					<div class="row">
						<label for="user-email" class="col-4 col-form-label">{{ $t('email') }}</label>
						<div class="col-8">
							<input
								id="user-email"
								class="form-control mb-2"
								type="email"
								maxlength="60"
								v-model="user.email"
								:class="{ 'is-invalid': !isValidEmail && !isUniqueUser }"
							/>
							<div v-if="!isUniqueUser" class="invalid-feedback">
								{{ $t('user.validEmail', { symbol: '@' }) }}
							</div>
						</div>
					</div>

					<div class="row">
						<label for="user-idcode" class="col-4 col-form-label">{{ $t('idCode') }}</label>
						<div class="col-8">
							<input
								id="user-idcode"
								class="form-control mb-2"
								type="text"
								maxlength="11"
								v-model="user.idCode"
								:class="{ 'is-invalid': !isValidIdCode && !isUniqueUser }"
							/>
							<div v-if="!isUniqueUser" class="invalid-feedback">
								{{ $t('user.validIdCode') }}
							</div>
						</div>
					</div>

					<hr />

					<input name="disable-pwd-mgr-1" type="password" id="disable-pwd-mgr-1" style="display: none" value="disable-pwd-mgr-1" />
					<input name="disable-pwd-mgr-2" type="password" id="disable-pwd-mgr-2" style="display: none" value="disable-pwd-mgr-2" />
					<input name="disable-pwd-mgr-3" type="password" id="disable-pwd-mgr-3" style="display: none" value="disable-pwd-mgr-3" />

					<div class="row">
						<label for="user-password" class="col-4 col-form-label">{{ $t('password') }}</label>
						<div class="col-8">
							<input
								id="user-password"
								class="form-control mb-2"
								type="password"
								maxlength="60"
								v-model="user.password"
								:class="{ 'is-invalid': !isValidPassword }"
							/>
							<div class="invalid-feedback">
								{{ $t('user.validPassword') }}
							</div>
						</div>
					</div>

					<div class="row">
						<label for="user-password2" class="col-4 col-form-label">{{ $t('password') }} x2</label>
						<div class="col-8">
							<input
								id="user-password2"
								class="form-control mb-2"
								type="password"
								maxlength="60"
								v-model="password2"
								:class="{ 'is-invalid': !isValidPassword2 }"
							/>
							<div class="invalid-feedback">
								{{ $t('user.validPassword2') }}
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col-12 d-flex justify-content-end">
							<div class="d-flex gap-2">
								<button data-test="user-reset-btn" class="btn btn-secondary flex-fill" @click="reset">{{ $t('misc.reset') }}</button>
								<button data-test="user-save-tn" class="btn btn-primary flex-fill" @click="save" :disabled="!canSave">
									{{ $t('misc.save') }}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
